import type { Chain } from '../../chain';
import { isWalletCreationRequirement } from '../isWalletCreationRequirement/isWalletCreationRequirement';
import type {
  WalletCreationBitcoinConfig,
  WalletCreationRequirement,
} from '../waas.types';

const DEFAULT_BITCOIN_ADDRESS_TYPE = 'native_segwit' as const;

type NormalizeWalletRequirementsParams = {
  defaultBitcoinConfig?: WalletCreationBitcoinConfig;
  input: WalletCreationRequirement[] | Chain[];
};

/**
 * Normalizes wallet creation input from either legacy Chain[] format
 * or new WalletCreationRequirement[] format to WalletCreationRequirement[].
 *
 * For BTC chains without explicit bitcoinConfig, defaults to native_segwit.
 *
 * @param params.input - Either an array of Chain strings or WalletCreationRequirement objects
 * @param params.defaultBitcoinConfig - Optional default bitcoin config to use for BTC chains when converting from legacy format
 * @returns Array of WalletCreationRequirement objects
 */
export const normalizeWalletRequirements = ({
  defaultBitcoinConfig,
  input,
}: NormalizeWalletRequirementsParams): WalletCreationRequirement[] => {
  if (input.length === 0) {
    return [];
  }

  const [firstItem] = input;

  if (isWalletCreationRequirement(firstItem)) {
    return (input as WalletCreationRequirement[]).map((requirement) => {
      if (requirement.chain === 'BTC' && !requirement.bitcoinConfig) {
        return {
          ...requirement,
          bitcoinConfig: { addressType: DEFAULT_BITCOIN_ADDRESS_TYPE },
        };
      }

      return requirement;
    });
  }

  return (input as Chain[]).map((chain) => {
    const requirement: WalletCreationRequirement = { chain };

    if (chain === 'BTC') {
      requirement.bitcoinConfig = defaultBitcoinConfig ?? {
        addressType: DEFAULT_BITCOIN_ADDRESS_TYPE,
      };
    }

    return requirement;
  });
};
